import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SecurityIcon from '@mui/icons-material/Security';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import { type FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const OnboardingLanding: FC = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<'car_dealership' | 'insurance_broker' | null>(null);

  const handleTypeSelection = (type: 'car_dealership' | 'insurance_broker') => {
    setSelectedType(type);
  };

  const handleContinue = () => {
    if (selectedType === 'car_dealership') {
      navigate('/onboarding/car-dealership');
    } else if (selectedType === 'insurance_broker') {
      navigate('/onboarding/insurance-broker');
    }
  };

  const companyTypes = [
    {
      type: 'car_dealership' as const,
      title: 'Concesionaria de Autos',
      description: 'Gestiona tu concesionaria con herramientas especializadas para la venta de vehículos',
      icon: <DirectionsCarIcon sx={{ fontSize: 60, color: 'inherit' }} />,
      color: '#1976d2',
    },
    {
      type: 'insurance_broker' as const,
      title: 'Corredora de Seguros',
      description: 'Administra tu corredora con soluciones diseñadas para el sector asegurador',
      icon: <SecurityIcon sx={{ fontSize: 60, color: 'inherit' }} />,
      color: '#2e7d32',
    },
  ];

  return (
    <Grid sx={{ minHeight: '100vh' }} container component="main">
      <Grid size={12} component={Paper} elevation={6} square>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: { xs: '100%', lg: '80%' },
            mx: 'auto',
            minHeight: '100vh',
            alignItems: 'center',
            pt: '60px',
            pb: 4,
          }}
        >
          <img alt="Relif" src="/logo.webp" height="80" />

          <Box sx={{ width: '100%', maxWidth: 600, mb: 4, pt: '28px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Paso 1 de 2
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                50%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={50} sx={{ height: 8, borderRadius: 4 }} />
          </Box>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              ¿Qué tipo de empresa eres?
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600 }}>
              Selecciona el tipo de empresa para personalizar tu experiencia y acceder a las herramientas más adecuadas
              para tu negocio.
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ mb: 4, maxWidth: 800 }}>
            {companyTypes.map((companyType) => (
              <Grid size={{ xs: 12, md: 6 }} key={companyType.type}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border:
                      selectedType === companyType.type ? `3px solid ${companyType.color}` : '3px solid transparent',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                    bgcolor: selectedType === companyType.type ? `${companyType.color}15` : 'background.paper',
                  }}
                  onClick={() => handleTypeSelection(companyType.type)}
                >
                  <CardContent
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        mb: 3,
                        color: selectedType === companyType.type ? companyType.color : 'text.secondary',
                        transition: 'color 0.3s ease-in-out',
                      }}
                    >
                      {companyType.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: selectedType === companyType.type ? companyType.color : 'text.primary',
                        transition: 'color 0.3s ease-in-out',
                      }}
                    >
                      {companyType.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {companyType.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <PrimaryBtn sx={{ px: 6, py: 1.5 }} variant="contained" onClick={handleContinue} disabled={!selectedType}>
              Continuar
            </PrimaryBtn>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default OnboardingLanding;
